import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import dayjs from 'dayjs';
import MLIcon from '../MLIcon';
import MLTooltip from '../MLTooltip';
import { GRADING_TYPE } from '../../utils/constants';
import {
  ASSIGNMENT_STATUS,
  ASSIGNMENT_STATUS_DISPLAY,
} from '../../utils/constants';
import s from './WeeklyAssignmentsGraph.module.css';

const WeeklyAssignmentsDisplay = ({
  assignments,
  onAssignmentClick,
  weekIsCompleted,
}) => {
  const ref = useRef([]);
  const [currentFocusIndex, setCurrentFocusIndex] = useState(0);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Only enable keyboard navigation if focus is here
      const focusedElement = document.activeElement;
      const isOnAssignmentElement = ref.current.includes(focusedElement);

      if (!isOnAssignmentElement) return;

      // Rotate focus on arrow keys
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        // Don't allow focus to go past the last assignment
        const newIndex = Math.min(
          currentFocusIndex + 1,
          assignments.length - 1,
        );
        setCurrentFocusIndex(newIndex);
        ref.current[newIndex].focus();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        // Don't allow focus to go before the first assignment
        const newIndex = Math.max(currentFocusIndex - 1, 0);
        setCurrentFocusIndex(newIndex);
        ref.current[newIndex].focus();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [currentFocusIndex, assignments]);

  // Map assignment statuses to colors
  const segmentClassNames = {
    [ASSIGNMENT_STATUS.COMPLETED]: s.completed,
    [ASSIGNMENT_STATUS.IN_PROGRESS]: s.inProgress,
    [ASSIGNMENT_STATUS.NOT_STARTED]: s.notStarted,
    [ASSIGNMENT_STATUS.MISSED]: s.overdue,
  };

  const formatAssignmentDue = (assignment) => {
    // Returns the day of the week the assignment is due
    return `Due ${dayjs(assignment.studentAssignmentDueAt).format('dddd')}`;
  };

  const formatAssignedPoints = (assignment) => {
    if (assignment.grading_type === GRADING_TYPE.UNGRADED) {
      return 'Ungraded';
    }
    return `${assignment.assigned_points} pts`;
  };

  const renderAssignments = () => {
    return (
      <div className={s.weeklyProgressAssignments}>
        {assignments.map((assignment, index) => (
          <MLTooltip
            key={assignment.id}
            content={
              <div className={s.tooltipContent}>
                {assignment.name}
                <br />
                {`${formatAssignmentDue(assignment)} | ${formatAssignedPoints(assignment)}`}
              </div>
            }
          >
            <button
              aria-label={`Assignment status: ${ASSIGNMENT_STATUS_DISPLAY[assignment.status]}`}
              onClick={(event) => onAssignmentClick(event, assignment)}
              tabIndex={index === currentFocusIndex ? 0 : -1}
              ref={(el) => (ref.current[index] = el)}
              data-test-id="progress_bar_student_assignment_launch"
              className={classNames(
                s.weeklyProgressAssignment,
                segmentClassNames[assignment.status],
              )}
            />
          </MLTooltip>
        ))}
      </div>
    );
  };

  const renderLegend = () => {
    const legendStatusesMap = {
      [ASSIGNMENT_STATUS.COMPLETED]: 0,
      [ASSIGNMENT_STATUS.IN_PROGRESS]: 0,
      [ASSIGNMENT_STATUS.MISSED]: 0,
      [ASSIGNMENT_STATUS.NOT_STARTED]: 0,
    };
    assignments.forEach((assignment) => {
      if (!legendStatusesMap[assignment.status]) {
        legendStatusesMap[assignment.status] = 0;
      }
      legendStatusesMap[assignment.status]++;
    });

    return (
      <div
        className={s.weeklyProgressLegend}
        aria-label="Assignment status totals"
      >
        {Object.keys(legendStatusesMap)
          .filter((status) => legendStatusesMap[status] > 0)
          .map((status) => (
            <div key={status} className={s.weeklyProgressLegendItem}>
              <div
                aria-hidden="true"
                className={classNames(
                  s.weeklyProgressLegendItemColor,
                  segmentClassNames[status],
                )}
              />
              <span className={s.weeklyProgressLegendItemText}>
                {legendStatusesMap[status]} {ASSIGNMENT_STATUS_DISPLAY[status]}
              </span>
            </div>
          ))}
      </div>
    );
  };

  return (
    <>
      <div className={s.assignmentsContainer}>
        {renderAssignments()}
        <MLIcon
          height="50"
          width="54"
          className={classNames(
            weekIsCompleted ? s.starCompleted : s.starNotCompleted,
          )}
          type="star"
        />
      </div>
      {renderLegend()}
    </>
  );
};

WeeklyAssignmentsDisplay.propTypes = {
  assignments: PropTypes.array.isRequired,
  onAssignmentClick: PropTypes.func.isRequired,
  weekIsCompleted: PropTypes.bool,
};

export default WeeklyAssignmentsDisplay;
